import React, { createContext, useContext, useMemo } from 'react';

import { useInclusionDrafts } from './tabs/inclusions/useInclusionDrafts';
import { useScrapeSession } from './tabs/scrape/useScrapeSession';

// Mounted once at PricingToolPage so state here survives tab switches - including
// useScrapeSession's poll timer, whose cleanup now fires only when this unmounts, not per-tab.
interface PricingToolSessionValue {
  inclusions: ReturnType<typeof useInclusionDrafts>;
  scrape: ReturnType<typeof useScrapeSession>;
}

const PricingToolSessionContext = createContext<PricingToolSessionValue | undefined>(undefined);

export function PricingToolSessionProvider({ offerId, children }: { offerId?: string; children: React.ReactNode }) {
  const inclusions = useInclusionDrafts(offerId);
  const scrape = useScrapeSession();
  const value = useMemo(() => ({ inclusions, scrape }), [inclusions, scrape]);

  return <PricingToolSessionContext.Provider value={value}>{children}</PricingToolSessionContext.Provider>;
}

export function usePricingToolSession() {
  const context = useContext(PricingToolSessionContext);

  if (!context) {
    throw new Error('usePricingToolSession must be used within a PricingToolSessionProvider.');
  }

  return context;
}

export function useInclusionDraftsContext() {
  return usePricingToolSession().inclusions;
}

export function useScrapeSessionContext() {
  return usePricingToolSession().scrape;
}
